import { getPhoneCallOutcomeLabel } from "../models/phoneCallOutcomes";
import "../styles/dashboard.css";
import { useMemo, useState, useEffect } from "react";
import useDealStore from "../states/useDealStore";
import useAuthStore from "../../states/authStore";
import NewContact from "../components/modals/NewContact";
import ImportContacts from "../components/modals/ImportContacts";
import TabView from "../components/TabView/TabView";
import TimeSelectorWidget from "../components/TimeSelectorWidget/TimeSelectorWidget";
import { DataTable, Page, QuickStatsWidget } from "@iq/models";
import { FiEye, FiPhone, FiUpload } from "react-icons/fi";
import { IoAdd } from "react-icons/io5";
import AddPhoneCall from "../components/modals/AddPhoneCall";
import CreateFollowUpModal from "../components/modals/CreateFollowUpModal";

import { getDashboardStats } from "../services/dashboard_service";
import PeopleSelectorWidget from "../components/PeopleSelectorWidget/PeopleSelectorWidget";

const DASHBOARD_TABLE_TABS = ["Activity", "Follow Up", "Open Deals", "Stale", "Incomplete"];
const DASHBOARD_FILTER_OPTIONS = ["Individual", "Group"];
const DASHBOARD_TIME_RANGE_OPTIONS = ["Today", "Week", "Quarter", "Year", "All Time"];

const PERIOD_QUICK_STATS = {
    Today: "day",
    Week: "week",
    Quarter: "quarter",
    Year: "year",
    "All Time": "all"
};

const STALE_CONTACT_DAYS = 30;

function formatDashboardDateTime(value) {
    if (!value || value === "-" || value === "N/A") {
        return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return parsed.toLocaleString();
}

function formatDashboardTouches(value) {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return String(value);
    }

    return String(parsed);
}

const DASHBOARD_CONTACTS_TABLE_CONFIG = {
    columns: [
        { 
            key: "status", 
            label: "Status", 
            accessor: (row) => row?.status || row?.stage || "-", 
            filterable: true, 
            quickValues: ["New", "Aware", "Discovery", "Demo", "Negotiation"],
            width: ".75fr"
        },
        { 
            key: "name", 
            label: "Name", 
            accessor: (row) => row?.name || `${row?.first_name || ""} ${row?.last_name || ""}`.trim() || "-", 
            filterable: true, 
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr" 
        },
        { 
            key: "account", 
            label: "Account", 
            accessor: (row) => row?.account || row?.organization_name || row?.company || "-", 
            filterable: true, 
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "2fr"  
        },
        { 
            key: "email", 
            label: "Email", 
            accessor: (row) => row?.email || "-", 
            filterable: false 
        },
        { 
            key: "phone", 
            label: "Phone", 
            accessor: (row) => row?.phone || row?.phone_number || row?.mobile || "-", 
            filterable: false 
        },
        {
            key: "last_call",
            label: "Last Call",
            accessor: (row) => formatDashboardDateTime(row?.last_call ?? row?.lastCall ?? row?.last_call_at ?? row?.last_call_time),
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
        {
            key: "last_email",
            label: "Last Email",
            accessor: (row) => formatDashboardDateTime(row?.last_email ?? row?.lastEmail ?? row?.last_email_at ?? row?.last_email_time),
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
        {
            key: "outcome",
            label: "Outcome",
            accessor: (row) => getPhoneCallOutcomeLabel(row?.outcome ?? row?.last_outcome ?? row?.lastOutcome ?? row?.last_contact_outcome),
            filterable: false
        },
        {
            key: "touches",
            label: "Touches",
            accessor: (row) => formatDashboardTouches(row?.touches ?? row?.touch_count ?? row?.touchCount ?? row?.touches_count),
            filterable: false,
        },
        {
            key: "next_touch",
            label: "Next Touch",
            accessor: (row) => formatDashboardDateTime(row?.next_touch ?? row?.nextTouch ?? row?.next_touch_at ?? row?.next_step_at),
            filterable: false
        },
    ],
    actionsColumnWidth: "2fr",
};

const DASHBOARD_DEALS_TABLE_CONFIG = {
    columns: [
        { 
            key: "stage", 
            label: "Stage", 
            accessor: (row) => row?.deal_stage || row?.stage || row?.status || "-", 
            filterable: false,
            width: ".75fr" 
        },
        { 
            key: "name", 
            label: "Name", 
            accessor: (row) => row?.contact_name || row?.manual_contact_name || row?.contact?.name || "-", 
            filterable: true, 
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr" 
        },
        { 
            key: "account", 
            label: "Account", 
            accessor: (row) => row?.account || row?.organization_name || row?.company || "-", 
            filterable: true, 
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "2fr" 
        },
        { 
            key: "email", 
            label: "Email Address", 
            accessor: (row) => row?.email || row?.contact_email || row?.contact?.email || "-", 
            filterable: false 
        },
        { 
            key: "phone", 
            label: "Phone", 
            accessor: (row) => row?.phone || row?.phone_number || row?.mobile || row?.contact_phone || row?.contact?.phone || "-", 
            filterable: false 
        },
        {
            key: "value",
            label: "Deal Value",
            accessor: (row) => {
                const cents = Number(row?.amount_cents);
                if (Number.isFinite(cents) && cents > 0) {
                    return `$${(cents / 100).toLocaleString()}`;
                }

                const rawValue = row?.deal_value ?? row?.value ?? row?.amount;
                if (rawValue === null || rawValue === undefined || rawValue === "") {
                    return "-";
                }

                const parsed = Number(rawValue);
                return Number.isNaN(parsed) ? String(rawValue) : `$${parsed.toLocaleString()}`;
            },
            filterable: false,
        },
        { 
            key: "last_call", 
            label: "Last Call", 
            accessor: (row) => formatDashboardDateTime(row?.last_call ?? row?.lastCall ?? row?.last_call_at ?? row?.last_call_time), 
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
        { 
            key: "outcome", 
            label: "Outcome", 
            accessor: (row) => getPhoneCallOutcomeLabel(row?.outcome ?? row?.last_outcome ?? row?.lastOutcome ?? row?.last_contact_outcome),
            filterable: false,
        },
        { 
            key: "last_email", 
            label: "Last Email", 
            accessor: (row) => formatDashboardDateTime(row?.last_email ?? row?.lastEmail ?? row?.last_email_at ?? row?.last_email_time), 
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
        { 
            key: "next_touch", 
            label: "Next Touch", 
            accessor: (row) => formatDashboardDateTime(row?.next_touch ?? row?.nextTouch ?? row?.next_touch_at ?? row?.next_step_at), 
            filterable: false
        },
        { 
            key: "touches", 
            label: "Touches", 
            accessor: (row) => formatDashboardTouches(row?.touches ?? row?.touch_count ?? row?.touchCount ?? row?.touches_count), 
            filterable: false 
        },
    ],
    actionsColumnWidth: "2fr",
};

function matchesRowOwner(row, userId, profileName) {
    const ownerUserId = row.owner_user_id ?? row.ownerUserId ?? row.owner_id ?? row.ownerId;

    if (ownerUserId != null && ownerUserId !== "") {
        return Number(ownerUserId) === Number(userId);
    }

    const ownerName = String(row.owner_name ?? row.ownerName ?? row.owner ?? "")
        .trim()
        .toLowerCase();
    const currentProfileName = String(profileName || "").trim().toLowerCase();

    if (ownerName && currentProfileName) {
        return ownerName === currentProfileName;
    }

    return false;
}

function buildDashboardRowsForTab(tab, rows, followUps) {
    switch (tab) {
        case "Follow Up": {
            const followUpContactIds = new Set();

            for (const item of Array.isArray(followUps) ? followUps : []) {
                const contactId = item?.contact_id ?? item?.contactId;
                if (contactId != null && contactId !== "") {
                    followUpContactIds.add(String(contactId));
                }
            }

            return rows
                .map((row) => {
                    const key = String(row?.id ?? row?.contact_id ?? row?.contactId ?? "");
                    const hasFollowUpReference = key && followUpContactIds.has(key);
                    const needsFollowUp = Boolean(row?.needsFollowUp ?? row?.needs_follow_up);
                    const stage = String(row?.stage ?? row?.stages ?? row?.status ?? "").trim().toLowerCase();
                    const stageNeedsFollowUp = stage === "aware" || stage === "discovery";
                    const includeRow = Boolean(hasFollowUpReference || needsFollowUp || stageNeedsFollowUp);

                    return {
                        row,
                        includeRow,
                        hasFollowUpReference,
                    };
                })
                .filter((item) => item.includeRow)
                .map((item) => {
                    if (item.hasFollowUpReference) {
                        return item.row;
                    }

                    return {
                        ...item.row,
                        isFollowUpSuggestion: true,
                        suggestionLabel: "Schedule Follow Up",
                    };
                });
        }
        case "Stale": {
            const staleCutoff = Date.now() - STALE_CONTACT_DAYS * 24 * 60 * 60 * 1000;

            const parseActivityDate = (value) => {
                if (!value || value === "N/A" || value === "-") {
                    return null;
                }

                const parsed = new Date(value);
                if (Number.isNaN(parsed.getTime())) {
                    return null;
                }

                return parsed;
            };

            return rows.filter((row) => {
                const lastCall = parseActivityDate(row?.last_call ?? row?.lastCall ?? row?.last_call_at ?? row?.last_call_time);
                const lastEmail = parseActivityDate(row?.last_email ?? row?.lastEmail);

                if (!lastCall || !lastEmail) {
                    return false;
                }

                return lastCall.getTime() < staleCutoff && lastEmail.getTime() < staleCutoff;
            });
        }
        case "Incomplete":
            return rows.filter((row) => {
                const firstName = String(row?.first_name ?? row?.firstName ?? "").trim();
                const lastName = String(row?.last_name ?? row?.lastName ?? "").trim();
                const renderedName = String(row?.name ?? row?.contact_name ?? `${firstName} ${lastName}`).trim();
                const account = String(row?.account ?? row?.account_name ?? row?.organization_name ?? row?.company ?? "").trim();
                const email = String(row?.email ?? row?.contact_email ?? "").trim();
                const phone = String(row?.phone ?? row?.phone_number ?? row?.phoneNumber ?? row?.mobile ?? row?.contact_phone ?? "").trim();

                return !renderedName || !account || !email || !phone;
            });
        case "Activity":
        default:
            return rows;
    }
}

function buildDashboardActivityRowActions(row, onContactSelect, setLogCallContact) {
    const contact = row?.contact ?? row;

    return [
        {
            id: "view-contact",
            label: "View",
            icon: <FiEye aria-hidden="true" />,
            onClick: () => onContactSelect?.(contact),
        },
        {
            id: "log-call",
            label: "Call",
            icon: <FiPhone aria-hidden="true" />,
            onClick: () => setLogCallContact?.(contact),
        },
    ];
}

function buildDashboardFollowUpRowActions(row, setFollowUpContact) {
    const contact = row?.contact ?? row;

    return [
        {
            id: "create-follow-up",
            label: "Follow Up",
            onClick: () => setFollowUpContact?.(contact),
        },
    ];
}

function buildDashboardDealRowActions(row, onContactSelect, setLogCallContact) {
    const contact = {
        id: row?.contact_id ?? row?.contactId ?? row?.contact?.id ?? null,
        name: row?.contact_name ?? row?.manual_contact_name ?? row?.contact?.name ?? "",
        account: row?.account ?? row?.organization_name ?? row?.company ?? "",
        email: row?.email ?? row?.contact_email ?? row?.contact?.email ?? "",
        phone: row?.phone ?? row?.phone_number ?? row?.mobile ?? row?.contact_phone ?? row?.contact?.phone ?? "",
    };
    const hasContactRef = Boolean(contact.id || contact.name);

    return [
        {
            id: "view-contact",
            label: "View",
            icon: <FiEye aria-hidden="true" />,
            onClick: () => onContactSelect?.(contact),
            disabled: !hasContactRef,
        },
        {
            id: "log-call",
            label: "Call",
            icon: <FiPhone aria-hidden="true" />,
            onClick: () => setLogCallContact?.(contact),
            disabled: !hasContactRef,
        },
    ];
}

function Dashboard({
    setPageHeader,
    onContactSelect,
    contacts = [],
    loadingContacts = false,
    onContactsRefresh,
    followUps = [],
    onFollowUpsRefresh,
}) {
    const userId = useAuthStore((state) => state.userId);
    const profileName = useAuthStore((state) => state.profile?.name || "");
    const deals = useDealStore((state) => state.deals);
    const loadingDeals = useDealStore((state) => state.loading);
    const [timeRange, setTimeRange] = useState("Week");
    const [activeDashboardTab, setActiveDashboardTab] = useState("Activity");
    const [showNewContact, setShowNewContact] = useState(false);
    const [showImportContact, setShowImportContact] = useState(false);
    const [logCallContact, setLogCallContact] = useState(null);
    const [followUpContact, setFollowUpContact] = useState(null);

    const [viewMode, setViewMode] = useState("Individual");

    const [stats, setStats] = useState({});
    const [loadingStats, setLoadingStats] = useState(false);

    useEffect(() => {
        async function loadStats() {
            setLoadingStats(true);
            try {
                const data = await getDashboardStats(PERIOD_QUICK_STATS[timeRange], viewMode);

                setStats({
                    callsCompleted: data.call_count,
                    callsGoal: 60,
                    emailsSent: data.email_count,
                    newDeals: data.new_deal_counts,
                    openDeals: data.open_deal_counts,
                    winRate: data.deals_win_rate,
                    totalSales: data.total_sales,
                    callsPerDay: data.calls_per_day || [0, 0, 0, 0, 0],
                });
            } catch (error) {
                console.error("Error loading dashboard stats:", error);
            } finally {
                setLoadingStats(false);
            }
        }

        loadStats();
    }, [timeRange, viewMode]);

    useEffect(() => {
        setPageHeader?.({
            title: "Dashboard",
            back: false,
            onBack: null,
            children: (
                <>
                    <div id="display-widgets">
                        <TimeSelectorWidget
                            value={timeRange}
                            onChange={setTimeRange}
                            options={DASHBOARD_TIME_RANGE_OPTIONS}
                        />
                        <PeopleSelectorWidget
                            value={viewMode}
                            onChange={setViewMode}
                            options={DASHBOARD_FILTER_OPTIONS}
                        />
                    </div>
                </>
            ),
        });
    }, [setPageHeader, timeRange, viewMode]);

    const dashboardRows = useMemo(() => {
        if (viewMode === "Group") {
            return contacts;
        }
        return contacts.filter((row) => matchesRowOwner(row, userId, profileName));
    }, [contacts, userId, profileName, viewMode]);

    const dashboardDeals = useMemo(() => {
        return deals.filter((row) => matchesRowOwner(row, userId, profileName));
    }, [deals, userId, profileName]);

    const dashboardTabViews = useMemo(() => {
        const dashboardHeaderActions = [
            {
                id: "import-contacts",
                label: "Import Contacts",
                color: "secondary",
                onClick: () => setShowImportContact(true),
                icon: <FiUpload aria-hidden="true" />,
            },
            {
                id: "new-contact",
                label: "New Contact",
                color: "accent",
                onClick: () => setShowNewContact(true),
                icon: <IoAdd aria-hidden="true" />,
            },
        ];

        return DASHBOARD_TABLE_TABS.reduce((views, tab) => {
            const isOpenDealsTab = tab === "Open Deals";
            const isActivityTab = tab === "Activity";
            const isFollowUpTab = tab === "Follow Up";
            const tableConfig = isOpenDealsTab ? DASHBOARD_DEALS_TABLE_CONFIG : DASHBOARD_CONTACTS_TABLE_CONFIG;
            const rowData = isOpenDealsTab ? dashboardDeals : buildDashboardRowsForTab(tab, dashboardRows, followUps);
            const rowActions = isOpenDealsTab
                ? (row) => buildDashboardDealRowActions(row, onContactSelect, setLogCallContact)
                : isActivityTab
                ? (row) => buildDashboardActivityRowActions(row, onContactSelect, setLogCallContact)
                : isFollowUpTab
                ? (row) => buildDashboardFollowUpRowActions(row, setFollowUpContact)
                : null;

            views[tab] = (
                <DataTable
                    rowData={rowData}
                    loading={isOpenDealsTab ? loadingDeals : loadingContacts}
                    tableConfig={tableConfig}
                    headerActions={dashboardHeaderActions}
                    onRowClick={isOpenDealsTab ? null : (contact) => onContactSelect?.(contact)}
                    rowActions={rowActions}
                />
            );

            return views;
        }, {});
    }, [dashboardDeals, dashboardRows, followUps, loadingContacts, loadingDeals, onContactSelect]);

    function handleCloseNewContact() {
        setShowNewContact(false);
        onContactsRefresh?.();
    }

    function handleCloseImportContacts() {
        setShowImportContact(false);
        onContactsRefresh?.();
    }

    return (
        <Page>
            <div className="dashboard-quick-stats">
                <p className="quick-stats-title">{`${timeRange === "Today" || timeRange === "All Time" ? "" : "Your"} ${timeRange}`}</p>
                <QuickStatsWidget stats={stats} loading={loadingStats} />
            </div>

            <TabView
                tabs={DASHBOARD_TABLE_TABS}
                views={dashboardTabViews}
                activeTab={activeDashboardTab}
                onTabChange={setActiveDashboardTab}
                ariaLabel="Dashboard table filters"
                className="dashboard-table-container"
            />

            {showNewContact && <NewContact onClose={handleCloseNewContact} />}
            {showImportContact && <ImportContacts onClose={handleCloseImportContacts} onImported={onContactsRefresh} />}

            {logCallContact && (
                <AddPhoneCall
                    isOpen={Boolean(logCallContact)}
                    onClose={() => setLogCallContact(null)}
                    deals={dashboardDeals}
                    contacts={[logCallContact]}
                    onCreated={() => {
                        setLogCallContact(null);
                        onContactsRefresh?.();
                    }}
                />
            )}

            <CreateFollowUpModal
                isOpen={Boolean(followUpContact)}
                contactId={followUpContact?.id ?? followUpContact?.contact_id ?? null}
                onClose={() => setFollowUpContact(null)}
                onCreated={() => {
                    setFollowUpContact(null);
                    onFollowUpsRefresh?.();
                    onContactsRefresh?.();
                }}
            />
        </Page>
    );
}

export default Dashboard;